import { useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { listCompanyMembers, type CompanyMember } from "@/shared/api/comments";
import {
  addDocAssignee,
  addDocTag,
  getDocMeta,
  listTags,
  removeDocAssignee,
  removeDocTag,
  type AssigneeRow,
  type TagDocType,
  type TagRow,
} from "@/shared/api/tags";
import { PersonAvatar } from "@/modules/shell/PersonAvatar";
import { softTagStyle } from "../tag-pill-style";

/** Doc header chips + popovers — plan/29, plan/35 */
export function TagsAssigneesPanel(props: {
  companySlug: string;
  docType: TagDocType;
  docId: string;
  linkPath?: string;
}) {
  const { companySlug, docType, docId, linkPath } = props;
  const [tags, setTags] = useState<TagRow[]>([]);
  const [assignees, setAssignees] = useState<AssigneeRow[]>([]);
  const [catalog, setCatalog] = useState<TagRow[]>([]);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [newTag, setNewTag] = useState("");
  const [tagOpen, setTagOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  const [peopleFilter, setPeopleFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tagRef = useRef<HTMLDivElement | null>(null);
  const peopleRef = useRef<HTMLDivElement | null>(null);

  async function reload() {
    const [meta, allTags, mem] = await Promise.all([
      getDocMeta(companySlug, docType, docId),
      listTags(companySlug),
      listCompanyMembers(companySlug),
    ]);
    setTags(meta.tags);
    setAssignees(meta.assignees);
    setCatalog(allTags.tags);
    setMembers(mem.members);
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : "Failed");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, docType, docId]);

  useEffect(() => {
    if (!tagOpen && !peopleOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setTagOpen(false);
        setPeopleOpen(false);
      }
    }
    function onPointer(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (tagOpen && tagRef.current && !tagRef.current.contains(t)) {
        setTagOpen(false);
      }
      if (peopleOpen && peopleRef.current && !peopleRef.current.contains(t)) {
        setPeopleOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [tagOpen, peopleOpen]);

  const availableTags = catalog.filter(
    (t) =>
      !tags.some((x) => x.id === t.id) &&
      t.name.toLowerCase().includes(tagFilter.trim().toLowerCase()),
  );
  const availablePeople = members.filter(
    (m) =>
      !assignees.some((a) => a.userId === m.id) &&
      m.label.toLowerCase().includes(peopleFilter.trim().toLowerCase()),
  );

  return (
    <section className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        {tags.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={busy}
            title="Remove tag"
            className="inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
            style={softTagStyle(t.color)}
            onClick={() => {
              setBusy(true);
              void removeDocTag(companySlug, { docType, docId, tagId: t.id })
                .then(() => reload())
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : "Failed"),
                )
                .finally(() => setBusy(false));
            }}
          >
            <span className="truncate">{t.name}</span>
            <span className="text-[var(--color-muted)]" aria-hidden>
              ×
            </span>
          </button>
        ))}

        {assignees.map((a) => (
          <button
            key={a.userId}
            type="button"
            disabled={busy}
            title={`Unassign ${a.label}`}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-background)]/80 py-0.5 pl-0.5 pr-2.5 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
            onClick={() => {
              setBusy(true);
              void removeDocAssignee(companySlug, {
                docType,
                docId,
                userId: a.userId,
              })
                .then(() => reload())
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : "Failed"),
                )
                .finally(() => setBusy(false));
            }}
          >
            <PersonAvatar label={a.label} size="sm" variant="solid" />
            <span className="truncate">{a.label}</span>
            <span className="text-[var(--color-muted-fg)]" aria-hidden>
              ×
            </span>
          </button>
        ))}

        <div className="relative" ref={tagRef}>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full text-[var(--color-muted)]"
            disabled={busy}
            onClick={() => {
              setPeopleOpen(false);
              setTagOpen((v) => !v);
            }}
          >
            Add tag
          </Button>
          {tagOpen && (
            <div className="absolute left-0 top-full z-20 mt-1.5 w-64 rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 shadow-[var(--shadow-float)] animate-[fadeIn_140ms_ease-out]">
              <Input
                className="rounded-xl"
                placeholder="Search or create…"
                value={tagFilter || newTag}
                autoFocus
                onChange={(e) => {
                  setTagFilter(e.target.value);
                  setNewTag(e.target.value);
                }}
              />
              <ul className="m-0 mt-2 max-h-40 list-none space-y-0.5 overflow-y-auto p-0">
                {availableTags.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="flex w-full items-center rounded-xl px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                      disabled={busy}
                      onClick={() => {
                        setBusy(true);
                        void addDocTag(companySlug, {
                          docType,
                          docId,
                          tagId: t.id,
                        })
                          .then(() => {
                            setTagOpen(false);
                            setTagFilter("");
                            setNewTag("");
                            return reload();
                          })
                          .catch((err: unknown) =>
                            setError(
                              err instanceof Error ? err.message : "Failed",
                            ),
                          )
                          .finally(() => setBusy(false));
                      }}
                    >
                      {t.name}
                    </button>
                  </li>
                ))}
              </ul>
              {newTag.trim() &&
              !catalog.some(
                (t) =>
                  t.name.toLowerCase() === newTag.trim().toLowerCase(),
              ) ? (
                <Button
                  type="button"
                  size="sm"
                  className="mt-2 w-full"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void addDocTag(companySlug, {
                      docType,
                      docId,
                      tagName: newTag.trim(),
                    })
                      .then(() => {
                        setTagOpen(false);
                        setTagFilter("");
                        setNewTag("");
                        return reload();
                      })
                      .catch((err: unknown) =>
                        setError(
                          err instanceof Error ? err.message : "Failed",
                        ),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  Create “{newTag.trim()}”
                </Button>
              ) : null}
            </div>
          )}
        </div>

        <div className="relative" ref={peopleRef}>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full text-[var(--color-muted)]"
            disabled={busy}
            onClick={() => {
              setTagOpen(false);
              setPeopleOpen((v) => !v);
            }}
          >
            Assign
          </Button>
          {peopleOpen && (
            <div className="absolute left-0 top-full z-20 mt-1.5 w-64 rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-2 shadow-[var(--shadow-float)] animate-[fadeIn_140ms_ease-out]">
              <Input
                className="rounded-xl"
                placeholder="Search people…"
                value={peopleFilter}
                autoFocus
                onChange={(e) => setPeopleFilter(e.target.value)}
              />
              <ul className="m-0 mt-2 max-h-44 list-none space-y-0.5 overflow-y-auto p-0">
                {availablePeople.length === 0 ? (
                  <li className="px-2.5 py-2 text-xs text-[var(--color-muted)]">
                    No matches
                  </li>
                ) : (
                  availablePeople.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                        disabled={busy}
                        onClick={() => {
                          setBusy(true);
                          void addDocAssignee(companySlug, {
                            docType,
                            docId,
                            userId: m.id,
                            linkPath,
                          })
                            .then(() => {
                              setPeopleOpen(false);
                              setPeopleFilter("");
                              return reload();
                            })
                            .catch((err: unknown) =>
                              setError(
                                err instanceof Error ? err.message : "Failed",
                              ),
                            )
                            .finally(() => setBusy(false));
                        }}
                      >
                        <PersonAvatar
                          label={m.label}
                          size="md"
                          variant="solid"
                        />
                        <span className="truncate">{m.label}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mt-3">
          {error}
        </Alert>
      )}
    </section>
  );
}
